/**
 * F40 (Wave 2 Track D — admin) — promotion detail.
 *
 * Route: `/promotions/:id`
 * Permission: `promotions:read` gates the page render. The Suspend / Restore
 *             CTAs are additionally gated on `promotions:moderate` (super_admin
 *             + content_moderator only — finance + support are read-only). The
 *             server re-enforces every mutation + audits it.
 *
 * Composition:
 *   - Header with status Badge + a "Suspended" Badge + Suspend/Restore CTA
 *   - Terms Card (discount / display mode / validity / caps)
 *   - Scope Card (eligibility Chips, or "applies to any booking")
 *   - Metrics Stat row (redemptions / discount / net / unique players)
 *   - Redemption ledger Table
 *   - SuspendPromotionDialog (mandatory reason)
 *
 * Suspension is ORTHOGONAL to the lifecycle `status` (sets `suspendedAt`); the
 * page reflects that — the status Badge is unchanged after a suspend, only the
 * "Suspended" Badge + banner appear.
 */
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Chip,
  CurrencyDisplay,
  EmptyState,
  PromoStatusBadge,
  Skeleton,
  Stat,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import type { PromotionRedemption } from '@bravemove/types';
import { adminPromotionsApi, type AdminPromotionDetailResponse } from '../../api/promotions';
import { useAuth } from '../../auth/useAuth';
import { SuspendPromotionDialog } from '../../components/promotions/SuspendPromotionDialog';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatDiscount(
  discountType: 'percent' | 'flat',
  discountValue: number,
  currency: string,
  locale: string,
): string {
  if (discountType === 'percent') {
    return `${(discountValue / 100).toLocaleString(locale)}%`;
  }
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(
      discountValue / 100,
    );
  } catch {
    return `${(discountValue / 100).toFixed(2)} ${currency}`;
  }
}

function formatDay(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function AdminPromotionDetailPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ readonly id: string }>();
  const auth = useAuth();
  const toast = useToast();

  const [data, setData] = useState<AdminPromotionDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [acting, setActing] = useState(false);

  const canModerate = auth.adminUser?.permissions?.includes('promotions:moderate') ?? false;

  const refresh = useCallback(async (promoId: string) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await adminPromotionsApi.getPromotion(promoId);
      if (resp === null) setNotFound(true);
      else setData(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!id) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh(id);
  }, [id, refresh]);

  if (!id) return <Navigate to="/promotions" replace />;

  const handleSuspend = async (reason: string) => {
    if (!data) return;
    setActing(true);
    try {
      const updated = await adminPromotionsApi.suspendPromotion(data.promotion.id, { reason });
      setData({ ...data, promotion: updated });
      setSuspendOpen(false);
      toast.push({
        tone: 'success',
        title: t('admin:promotion.detail.suspendSuccess', { defaultValue: 'Promotion suspended.' }),
      });
    } catch {
      toast.push({
        tone: 'danger',
        title: t('admin:promotion.detail.actionError', {
          defaultValue: 'Could not complete the action.',
        }),
      });
    } finally {
      setActing(false);
    }
  };

  const handleRestore = async () => {
    if (!data) return;
    setActing(true);
    try {
      const updated = await adminPromotionsApi.restorePromotion(data.promotion.id);
      setData({ ...data, promotion: updated });
      toast.push({
        tone: 'success',
        title: t('admin:promotion.detail.restoreSuccess', { defaultValue: 'Promotion restored.' }),
      });
    } catch {
      toast.push({
        tone: 'danger',
        title: t('admin:promotion.detail.actionError', {
          defaultValue: 'Could not complete the action.',
        }),
      });
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl flex flex-col gap-4" data-testid="promotion-detail-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="max-w-3xl">
        <EmptyState
          title={t('admin:promotion.detail.notFound', { defaultValue: 'Promotion not found.' })}
          action={
            <Button variant="ghost" onClick={() => navigate('/promotions')}>
              {t('admin:promotion.detail.back', { defaultValue: 'Back to promotions' })}
            </Button>
          }
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl">
        <Banner tone="danger">
          {t('admin:promotion.detail.loadError', {
            defaultValue: 'Could not load this promotion.',
          })}
        </Banner>
      </div>
    );
  }

  const { promotion: promo, metrics, redemptions } = data;
  const isSuspended = promo.suspendedAt !== null;
  const scope = promo.scope ?? {};
  const hasScope = Boolean(
    (scope.sportNames && scope.sportNames.length > 0) ||
    (scope.subVenueIds && scope.subVenueIds.length > 0) ||
    (scope.daysOfWeek && scope.daysOfWeek.length > 0) ||
    scope.timeStart,
  );

  const redemptionColumns: readonly TableColumn<PromotionRedemption>[] = [
    {
      key: 'booking',
      header: t('admin:promotion.detail.colBooking', { defaultValue: 'Booking' }),
      cell: (row) => <span className="font-mono text-[11.5px] text-ink-2">{row.bookingId}</span>,
    },
    {
      key: 'user',
      header: t('admin:promotion.detail.colUser', { defaultValue: 'Player' }),
      cell: (row) => <span className="font-mono text-[11.5px] text-ink-2">{row.userId}</span>,
    },
    {
      key: 'gross',
      header: t('admin:promotion.detail.colGross', { defaultValue: 'Gross' }),
      align: 'end',
      cell: (row) => (
        <CurrencyDisplay
          amount={row.grossPriceMinor}
          currency={promo.discountCurrency}
          locale={locale}
        />
      ),
    },
    {
      key: 'discount',
      header: t('admin:promotion.detail.colDiscount', { defaultValue: 'Discount' }),
      align: 'end',
      cell: (row) => (
        <CurrencyDisplay
          amount={row.discountAmountMinor}
          currency={promo.discountCurrency}
          locale={locale}
        />
      ),
    },
    {
      key: 'net',
      header: t('admin:promotion.detail.colNet', { defaultValue: 'Net' }),
      align: 'end',
      cell: (row) => (
        <CurrencyDisplay
          amount={row.netPriceMinor}
          currency={promo.discountCurrency}
          locale={locale}
        />
      ),
    },
    {
      key: 'redeemedAt',
      header: t('admin:promotion.detail.colRedeemedAt', { defaultValue: 'Redeemed' }),
      cell: (row) => (
        <span className="text-[11.5px] text-ink-2">{formatDateTime(row.redeemedAt, locale)}</span>
      ),
    },
    {
      key: 'reversed',
      header: t('admin:promotion.detail.colReversed', { defaultValue: 'Reversed' }),
      cell: (row) =>
        row.reversedAt !== null ? (
          <Badge tone="warning" size="sm">
            {t('admin:promotion.detail.reversed', { defaultValue: 'Reversed' })}
          </Badge>
        ) : (
          <span className="text-ink-3">—</span>
        ),
    },
  ];

  return (
    <div className="max-w-4xl flex flex-col gap-4" data-testid="promotion-detail-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <button
            type="button"
            className="text-[12px] text-accent hover:underline self-start focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-accent/40 rounded"
            onClick={() => navigate('/promotions')}
          >
            {t('admin:promotion.detail.back', { defaultValue: 'Back to promotions' })}
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-h1 font-bold text-ink">
              {promo.code ??
                t('admin:promotion.list.autoNoCode', { defaultValue: 'Auto (no code)' })}
            </h1>
            <PromoStatusBadge
              size="sm"
              status={promo.status}
              label={t(`promotion:status.${promo.status}`, { defaultValue: promo.status })}
            />
            {isSuspended ? (
              <PromoStatusBadge
                size="sm"
                status="suspended_by_admin"
                label={t('admin:promotion.list.suspendedBadge', { defaultValue: 'Suspended' })}
                data-testid="promotion-detail-suspended-badge"
              />
            ) : null}
          </div>
          <span className="text-[12px] text-ink-3">{promo.venueName ?? promo.venueId}</span>
        </div>

        {/* Moderation action — gated on promotions:moderate */}
        {canModerate ? (
          <div className="flex items-center gap-2" data-testid="promotion-moderation-actions">
            {isSuspended ? (
              <Button
                variant="primary"
                onClick={handleRestore}
                loading={acting}
                data-testid="promotion-restore-cta"
              >
                {t('admin:promotion.detail.restore', { defaultValue: 'Restore' })}
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={() => setSuspendOpen(true)}
                data-testid="promotion-suspend-cta"
              >
                {t('admin:promotion.detail.suspend', { defaultValue: 'Suspend' })}
              </Button>
            )}
          </div>
        ) : (
          <div data-testid="promotion-readonly-note">
            <Banner tone="info">
              {t('admin:promotion.detail.readonlyNote', {
                defaultValue:
                  'You have read-only access to promotions. Suspend / restore require the promotions:moderate permission.',
              })}
            </Banner>
          </div>
        )}
      </header>

      {isSuspended ? (
        <div data-testid="promotion-suspended-banner">
          <Banner tone="warning">
            {t('admin:promotion.detail.suspendedBanner', {
              defaultValue: 'This promotion is suspended.',
            })}
            {promo.suspensionReason
              ? ` ${t('admin:promotion.detail.suspendedReason', {
                  reason: promo.suspensionReason,
                  defaultValue: 'Reason: {reason}',
                })}`
              : ''}
          </Banner>
        </div>
      ) : null}

      {/* Terms */}
      <Card>
        <div className="flex flex-col gap-3" data-testid="promotion-terms">
          <h2 className="text-h2 font-bold text-ink">
            {t('admin:promotion.detail.terms', { defaultValue: 'Promotion terms' })}
          </h2>
          <dl className="grid grid-cols-[150px_1fr] gap-y-2 gap-x-3 text-[12.5px]">
            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('admin:promotion.detail.discount', { defaultValue: 'Discount' })}
            </dt>
            <dd className="text-ink font-semibold">
              {formatDiscount(
                promo.discountType,
                promo.discountValue,
                promo.discountCurrency,
                locale,
              )}
              <span className="ms-2 text-ink-3 font-normal">
                (
                {t(`promotion:discountType.${promo.discountType}`, {
                  defaultValue: promo.discountType,
                })}
                )
              </span>
            </dd>

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('admin:promotion.detail.displayMode', { defaultValue: 'Display mode' })}
            </dt>
            <dd className="text-ink">
              {t(`promotion:displayMode.${promo.displayMode}`, { defaultValue: promo.displayMode })}
            </dd>

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('admin:promotion.detail.validity', { defaultValue: 'Validity' })}
            </dt>
            <dd className="text-ink">
              {formatDay(promo.validFrom, locale)} – {formatDay(promo.validUntil, locale)}
            </dd>

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('admin:promotion.detail.caps', { defaultValue: 'Usage limits' })}
            </dt>
            <dd className="text-ink">
              {t('admin:promotion.detail.totalCap', { defaultValue: 'Total uses' })}:{' '}
              {promo.totalUsesCap ?? '∞'} ·{' '}
              {t('admin:promotion.detail.perUserCap', {
                defaultValue: 'Per-user uses',
              })}
              : {promo.perUserUsesCap ?? '∞'}
            </dd>
          </dl>
        </div>
      </Card>

      {/* Scope / eligibility */}
      <Card>
        <div className="flex flex-col gap-3" data-testid="promotion-scope">
          <h2 className="text-h2 font-bold text-ink">
            {t('admin:promotion.detail.scope', { defaultValue: 'Eligibility' })}
          </h2>
          {hasScope ? (
            <div className="flex flex-col gap-2.5 text-[12.5px]">
              {scope.sportNames && scope.sportNames.length > 0 ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-ink-3 text-[11px] uppercase tracking-wider">
                    {t('admin:promotion.detail.scopeSports', { defaultValue: 'Sports' })}
                  </span>
                  {scope.sportNames.map((s) => (
                    <Chip key={s}>{s}</Chip>
                  ))}
                </div>
              ) : null}
              {scope.subVenueIds && scope.subVenueIds.length > 0 ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-ink-3 text-[11px] uppercase tracking-wider">
                    {t('admin:promotion.detail.scopeCourts', { defaultValue: 'Courts' })}
                  </span>
                  {scope.subVenueIds.map((c) => (
                    <Chip key={c}>{c}</Chip>
                  ))}
                </div>
              ) : null}
              {scope.daysOfWeek && scope.daysOfWeek.length > 0 ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-ink-3 text-[11px] uppercase tracking-wider">
                    {t('admin:promotion.detail.scopeDays', { defaultValue: 'Days' })}
                  </span>
                  {scope.daysOfWeek.map((d) => (
                    <Chip key={d}>{DAY_LABELS[d] ?? String(d)}</Chip>
                  ))}
                </div>
              ) : null}
              {scope.timeStart && scope.timeEnd ? (
                <div className="flex items-center gap-2">
                  <span className="text-ink-3 text-[11px] uppercase tracking-wider">
                    {t('admin:promotion.detail.scopeTime', { defaultValue: 'Time window' })}
                  </span>
                  <Chip>
                    {scope.timeStart} – {scope.timeEnd}
                  </Chip>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-[12.5px] text-ink-2">
              {t('admin:promotion.detail.scopeAny', {
                defaultValue: 'Applies to any booking at this venue',
              })}
            </p>
          )}
        </div>
      </Card>

      {/* Metrics */}
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="promotion-metrics">
        <Stat
          label={t('admin:promotion.detail.totalRedemptions', { defaultValue: 'Redemptions' })}
          value={metrics.totalRedemptions.toLocaleString(locale)}
        />
        <Stat
          label={t('admin:promotion.detail.totalDiscount', { defaultValue: 'Discount given' })}
          value={
            <CurrencyDisplay
              amount={metrics.totalDiscountMinor}
              currency={promo.discountCurrency}
              locale={locale}
            />
          }
        />
        <Stat
          label={t('admin:promotion.detail.totalNet', { defaultValue: 'Net collected' })}
          value={
            <CurrencyDisplay
              amount={metrics.totalNetMinor}
              currency={promo.discountCurrency}
              locale={locale}
            />
          }
        />
        <Stat
          label={t('admin:promotion.detail.distinctUsers', { defaultValue: 'Unique players' })}
          value={metrics.distinctUsers.toLocaleString(locale)}
        />
      </section>

      {/* Redemption ledger */}
      <section className="space-y-3" data-testid="promotion-redemptions">
        <h2 className="text-[14px] font-semibold text-ink">
          {t('admin:promotion.detail.redemptions', { defaultValue: 'Redemptions' })}
        </h2>
        {redemptions.length === 0 ? (
          <EmptyState
            title={t('admin:promotion.detail.redemptionsEmpty', {
              defaultValue: 'No redemptions yet.',
            })}
          />
        ) : (
          <Table
            data={redemptions as PromotionRedemption[]}
            columns={redemptionColumns as TableColumn<PromotionRedemption>[]}
            rowKey={(row) => row.id}
          />
        )}
      </section>

      <SuspendPromotionDialog
        open={suspendOpen}
        loading={acting}
        onClose={() => setSuspendOpen(false)}
        onConfirm={handleSuspend}
      />
    </div>
  );
}

export default AdminPromotionDetailPage;
